import axiosInstance from './axiosConfig';

/**
 * 歌広告（Song Ad Video）API クライアント。
 *
 * プロジェクト = 楽曲（歌詞+曲）+ シーン配列（絵コンテ/クリップ）+ 合成済み動画。
 * ムービー制作（movieService.ts）と同じく、生成ジョブの進捗は
 * GET /song-ads/{id} のポーリングだけで追跡する（シーンごとに個別ポーリングしない）。
 */

export type SongAdProjectStatus =
  | 'draft'
  | 'lyrics_generating'
  | 'lyrics_ready'
  | 'song_generating'
  | 'song_ready'
  | 'song_failed'
  | 'scenes_ready'
  | 'composing'
  | 'completed'
  | 'compose_failed';

/** シーン段階の状態（採用済み成果物とジョブ状態から導出される。movie の SceneStageStatus と同一） */
export type SceneStageStatus = 'none' | 'pending' | 'processing' | 'completed' | 'failed';

/** vocal_gender: 'auto'（おまかせ）はv3で追加。既存の'male'/'female'は維持 */
export type VocalGender = 'auto' | 'male' | 'female';
export type Tempo = 'fast' | 'normal' | 'slow';
export type AspectRatio = '9:16' | '16:9' | '1:1';
export type TelopPosition = 'top' | 'middle' | 'bottom';
export type TelopSize = 'sm' | 'md' | 'lg';

/** CM構成タイプ（spec §7.1）。キー・ラベル対応は GOAL.md「API変更仕様の要点」が正（mockup.htmlの内部キーとは異なる） */
export type CmType = 'catchcopy' | 'balance' | 'explain';

/** 曲調・雰囲気タグ8種（spec §7.2）。キーは GOAL.md「API変更仕様の要点」が正 */
export type MoodTag =
  | 'bright'
  | 'fresh'
  | 'energetic'
  | 'funny'
  | 'cute'
  | 'stylish'
  | 'emotional'
  | 'premium';

/**
 * 動画の長さ（spec §7.5）。
 * v6 F7: **15秒はLyriaが尺を扱えず歌詞の大半が入らないため廃止**（実測: 15秒・3行指定で
 * Lyriaが102秒超に引き延ばし、行境界カットで3行中1行しか入らなかった。歌詞が少ないほど
 * Lyriaが引き延ばす性質があり、行数を減らしても悪化する）。新規作成・編集では選べない。
 * 既存プロジェクトに `target_seconds=15` のものが残っているため、`SongAdProject.target_seconds`
 * 自体は（表示のみ目的で）`number` のまま緩く保つ。フォーム編集時は30以上へ寄せる
 * （`normalizeTargetSeconds` in InfoStep.tsx）。
 */
export type TargetSeconds = 30 | 45 | 60;

/**
 * 音楽ジャンル12種（v5 c、v6でメタル・ハードコアを追加）。「曲調・雰囲気」8種（MoodTag）とは別軸で併存し、両方がLyriaプロンプトへ
 * 反映される。未指定（null）＝おまかせ。キーは GOAL.md「API変更仕様の要点」が正
 */
export type GenreTag =
  | 'pop'
  | 'rock'
  | 'ballad'
  | 'edm'
  | 'acoustic'
  | 'hiphop'
  | 'citypop'
  | 'anime'
  | 'jazz'
  | 'lofi'
  | 'metal'
  | 'hardcore';

/** シーンのモーション設定（spec §11）。v2の boolean `animate` を置き換える */
export type MotionMode = 'clip' | 'still' | 'skip';

export interface SongAdAdvancedSettings {
  required_words: string[];
  banned_words: string[];
  cta: string | null;
  /**
   * CTAロゴの明示指定（v6 F2/B1）。自組織の商品画像・アップロード画像のS3キー。
   * null＝未指定（backendが `brand_logo` → `product_image_keys[0]` の順で自動解決する）。
   */
  logo_image_key: string | null;
}

/** 歌詞のセクション構造（spec §8.3）。generate-lyrics のレスポンスに付与される */
export interface SongAdLyricsSection {
  label: 'intro' | 'verse' | 'chorus' | 'outro';
  line_indexes: number[];
}

/**
 * 歌詞行ごとの歌唱タイミング（v4 B1/F8）。`project.lyrics_kana` の行とWhisper転写語ストリームの
 * アライメント結果で、scenesに依存しない（シーン生成前でも取得できる）。
 * `matched=false` は歌い飛ばされた行（前後から補間した時刻が入る。テロップ表示には使わない）。
 */
export interface LyricLineTiming {
  index: number;
  text: string;
  kana: string;
  start: number;
  end: number;
  matched: boolean;
}

/**
 * 末尾CTAカードの解決済み内容（v4 F11、v6 F2でロゴ明示指定に対応）。backend
 * `SongAdCtaCardBuilder::content()` が実際にレンダリング時に使う値そのもの（`text`:
 * advanced_settings.cta → 商品名 → null、`logo_url`: advanced_settings.logo_image_key
 * （明示指定）→ 商品画像 category=brand_logo（明るいトーン優先）→ product_image_keys[0] → null
 * の解決済みURL）。`enabled` は `cta_enabled` と同値（互換のため `cta_enabled` も残る）。
 */
export interface SongAdCtaSettings {
  enabled: boolean;
  text: string | null;
  logo_url: string | null;
}

export type CaptionStyle = 'simple' | 'pop' | 'impact' | 'premium';

/** テロップ1件（spec §16.1）。詳細編集タイムラインの正本データ */
export interface CaptionSegment {
  id: string;
  text: string;
  start: number;
  end: number;
  style: CaptionStyle;
  position: TelopPosition;
  size: TelopSize;
}

/** 詳細編集タイムラインの正本JSON（song_ad_projects.timeline） */
export interface SongAdTimeline {
  captions: CaptionSegment[];
  scene_order: string[];
  motion: Record<string, { mode: MotionMode; prompt: string | null }>;
  updated_client_at: string;
}

export interface SongAdSceneStoryboard {
  status: SceneStageStatus;
  job_id: string | null;
  error_message: string | null;
  image: { id: number; url: string; s3_key: string } | null;
}

export interface SongAdSceneClip {
  status: SceneStageStatus;
  job_id: string | null;
  progress_percentage: number | null;
  progress_step: string | null;
  error_message: string | null;
  video: { id: number; url: string; duration_seconds: number } | null;
}

export interface SongAdScene {
  id: string;
  display_order: number;
  lyric_lines: string[];
  storyboard_prompt: string | null;
  clip_prompt: string | null;
  motion_mode: MotionMode;
  motion_prompt: string | null;
  telop_position: TelopPosition;
  telop_size: TelopSize;
  storyboard: SongAdSceneStoryboard;
  clip: SongAdSceneClip;
}

/**
 * 一覧・詳細どちらのレスポンスも表す共通型。
 * `scenes` は詳細取得（GET /song-ads/{id}）のときのみ含まれる。
 * `thumbnail_url` 等の一覧用の補助フィールドはサーバー側の実装差を吸収できるよう任意にしている。
 */
/**
 * オートパイロット（自動完走）の進行状況（v7・広告自動化マンからの起動用）。
 * `serializeProject()` が常に含めるため、一覧・詳細どちらの取得でも入っている。
 * stage は自動作成が進行中のときだけ意味を持つ値で、未知の値が来ても表示側が
 * 落ちないようフォールバックを用意すること（backendはstageを増やすことがある）。
 */
export interface SongAdAutopilot {
  status: 'running' | 'completed' | 'failed' | null;
  motion_mode: MotionMode | null;
  stage: 'song' | 'timings' | 'scenes' | 'storyboards' | 'clips' | 'compose' | string | null;
  error_message: string | null;
  started_at: string | null;
  deadline_at: string | null;
}

export interface SongAdProject {
  id: string;
  title: string;
  status: SongAdProjectStatus;
  product_id: number | null;
  product_image_keys: string[];
  aspect_ratio: AspectRatio;
  /** 音楽ジャンル（v5 c）。null＝おまかせ（曲調・雰囲気のみでLyriaプロンプトを合成） */
  genre_tag: GenreTag | null;
  style_tags: string[];
  style_free_text: string | null;
  vocal_gender: VocalGender;
  tempo: Tempo;
  /**
   * v6 F7: `TargetSeconds`（30|45|60）ではなく素の `number` のままにしてある。
   * 15秒指定の既存プロジェクトが staging に残っており、狭い型にすると読み取り時に型エラーになるため
   * （15秒は廃止済みで新規作成・編集では選べないが、表示自体は壊さない）。
   */
  target_seconds: number;
  cm_type: CmType;
  intro: boolean;
  advanced_settings: SongAdAdvancedSettings;
  lyrics: string | null;
  lyrics_kana: string | null;
  lyrics_fixed_at: string | null;
  /** generate-lyrics のレスポンスに付与されるセクション構造（spec §8.3）。未取得時は null */
  lyrics_sections: SongAdLyricsSection[] | null;
  /** 歌詞行ごとの歌唱タイミング（v4 B1）。未取得時は空配列 */
  lyric_timings: LyricLineTiming[];
  /** 末尾CTAカードの自動付与ON/OFF（v4 D/B5）。既定true。`cta.enabled` と同値（互換のため残す） */
  cta_enabled: boolean;
  /** 末尾CTAカードの解決済み内容（v4 F11） */
  cta: SongAdCtaSettings;
  song_url: string | null;
  song_duration_sec: number | null;
  song_error_message: string | null;
  /** 詳細編集タイムラインの正本。captions/init 前や曲再生成直後は null */
  timeline: SongAdTimeline | null;
  final_video_url: string | null;
  compose_error_message: string | null;
  composed_at: string | null;
  thumbnail_url?: string | null;
  scene_count?: number;
  storyboard_done_count?: number;
  clip_done_count?: number;
  scenes?: SongAdScene[];
  /**
   * オートパイロットの進行状況（v7）。serializeProject は常に含めるが、
   * 既存のテスト用フィクスチャ（EditorStep.test.tsx等）がこのフィールドを持たない
   * ものを多数使っているため、型としては scenes と同じくoptionalにしておく。
   */
  autopilot?: SongAdAutopilot;
  created_at: string;
  updated_at: string;
}

export interface CreateSongAdProjectParams {
  /** v5 d: 任意入力。未送信/空ならサーバー側で商品名＋「歌広告」＋日付から自動命名される */
  title?: string;
  product_id?: number;
  product_image_keys?: string[];
  aspect_ratio?: AspectRatio;
  /** 音楽ジャンル（v5 c）。未指定＝おまかせ */
  genre_tag?: GenreTag;
  style_tags?: string[];
  style_free_text?: string;
  vocal_gender?: VocalGender;
  tempo?: Tempo;
  target_seconds?: TargetSeconds;
  cm_type?: CmType;
  intro?: boolean;
  advanced_settings?: SongAdAdvancedSettings;
}

/**
 * PATCH /song-ads/{id}。①情報登録タブ（作成後の再編集。v5でモーダルから移設）もこれで送る。
 * `product_id`/`product_image_keys` は既存フィールドをそのままPATCHにも使う想定（v5 FA1）。
 */
export interface UpdateSongAdProjectParams {
  title?: string;
  product_id?: number | null;
  product_image_keys?: string[];
  aspect_ratio?: AspectRatio;
  /** 音楽ジャンル（v5 c）。null送信＝おまかせに戻す */
  genre_tag?: GenreTag | null;
  style_tags?: string[];
  style_free_text?: string;
  vocal_gender?: VocalGender;
  tempo?: Tempo;
  target_seconds?: TargetSeconds;
  cm_type?: CmType;
  intro?: boolean;
  advanced_settings?: SongAdAdvancedSettings;
  /** 末尾CTAカードの自動付与ON/OFF（v4 D/B5）。⑤プレビューのCTAトグルから送る */
  cta_enabled?: boolean;
}

/**
 * シーン一括更新（PUT /song-ads/{id}/scenes）の1要素。
 * 送った配列の順 = 新display_order。id なし = 新規シーン。送られなかった既存id = 削除。
 */
export interface UpdateSongAdSceneInput {
  id?: string;
  lyric_lines?: string[];
  motion_mode?: MotionMode;
  motion_prompt?: string | null;
  telop_position?: TelopPosition;
  telop_size?: TelopSize;
}

/**
 * 歌広告用ローカル画像アップロード（v5 b / B6）。base64（data URLプレフィックス無し）を送り、
 * S3キーを受け取る。商品未選択でも使えるよう project_id に依存しない（プロジェクト作成前の
 * 作成パネルからも呼べる）。
 *
 * API契約は tasks.md B6 実行メモが正（`POST /api/song-ads/upload-image`。axiosInstanceの
 * baseURLが `/api` を含むため相対パスは `/song-ads/upload-image`）。201で
 * `{ success, data: { s3_key, url, mime_type, file_size } }`、422で
 * `{ success:false, error: 'invalid_image_data'|'unsupported_image_type'|'image_too_large', message }`。
 */
const UPLOAD_IMAGE_PATH = '/song-ads/upload-image';

/** 画像ファイルをbase64（data URLプレフィックス無し）に変換する */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
    reader.readAsDataURL(file);
  });

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || 'エラーが発生しました';

/** 402 credit_insufficient エラーかどうかを判定する（グローバルリスナーは存在しないためページ側で使う） */
const isCreditInsufficientError = (error: any): boolean =>
  error?.response?.status === 402 || error?.response?.data?.error === 'credit_insufficient';

/** 422 kana_line_mismatch エラーかどうかを判定する（かな歌詞の行数不一致） */
const isKanaLineMismatchError = (error: any): boolean =>
  error?.response?.status === 422 && error?.response?.data?.error === 'kana_line_mismatch';

/** POST /song-ads/{id}/autopilot のクレジット概算（SongAdCreditGate::planEstimate と同じ形） */
export interface SongAdCreditPlanEstimate {
  scene_count: number;
  song: number;
  storyboards: number;
  clips: number;
  total: number;
}

export interface SongAdAutopilotShortfall {
  requiredCredits: number;
  availableCredits: number;
  estimate: SongAdCreditPlanEstimate;
}

/**
 * startAutoPilot が 402 credit_insufficient で失敗したときの詳細を取り出す。
 * それ以外のエラー（403/404/409/422等）は null を返すので、呼び出し側は
 * error.response.status / error.response.data.message で個別に文面を出すこと。
 */
const extractAutopilotCreditShortfall = (error: any): SongAdAutopilotShortfall | null => {
  if (!isCreditInsufficientError(error)) return null;
  const details = error?.response?.data?.details;
  if (!details) return null;
  return {
    requiredCredits: details.required_credits,
    availableCredits: details.available_credits,
    estimate: details.estimate,
  };
};

const songAdService = {
  async getProjects(): Promise<SongAdProject[]> {
    const response = await axiosInstance.get('/song-ads');
    return response.data.data.projects;
  },

  async createProject(params: CreateSongAdProjectParams): Promise<SongAdProject> {
    const response = await axiosInstance.post('/song-ads', params);
    return response.data.data.project;
  },

  /** ローカル画像をアップロードしS3キーを取得する（v5 b / B6）。File から呼ぶ想定 */
  async uploadImage(file: File): Promise<{ s3Key: string }> {
    const base64 = await fileToBase64(file);
    const response = await axiosInstance.post(UPLOAD_IMAGE_PATH, { image_data: base64 });
    return { s3Key: response.data.data.s3_key };
  },

  async getProject(id: string): Promise<SongAdProject> {
    const response = await axiosInstance.get(`/song-ads/${id}`);
    return response.data.data.project;
  },

  async updateProject(id: string, params: UpdateSongAdProjectParams): Promise<SongAdProject> {
    const response = await axiosInstance.patch(`/song-ads/${id}`, params);
    return response.data.data.project;
  },

  async deleteProject(id: string): Promise<void> {
    await axiosInstance.delete(`/song-ads/${id}`);
  },

  /** 歌詞生成（同期、〜30s）。instruction 指定でAI修正チップ・自由入力からの再生成に使う */
  async generateLyrics(id: string, instruction?: string): Promise<SongAdProject> {
    const response = await axiosInstance.post(`/song-ads/${id}/generate-lyrics`, {
      ...(instruction ? { instruction } : {}),
    });
    return response.data.data.project;
  },

  /**
   * 歌詞編集+フィックス。
   * lyricsKana を指定すると、その内容がそのまま保存される（行数が lyrics と一致しない場合は 422 `kana_line_mismatch`）。
   * 省略時は現行どおりサーバー側でかな歌詞が再生成される。
   */
  async updateLyrics(id: string, lyrics: string, lyricsKana?: string): Promise<SongAdProject> {
    const response = await axiosInstance.put(`/song-ads/${id}/lyrics`, {
      lyrics,
      ...(lyricsKana !== undefined ? { lyrics_kana: lyricsKana } : {}),
    });
    return response.data.data.project;
  },

  /** 曲生成（非同期job）。credit.check:song_ad_song で 402 の可能性あり */
  async generateSong(id: string): Promise<SongAdProject> {
    const response = await axiosInstance.post(`/song-ads/${id}/generate-song`);
    return response.data.data.project;
  },

  /** シーン分割（同期LLM、〜30s）。歌詞フィックス済み必須（未フィックスは422） */
  async generateScenes(id: string): Promise<SongAdProject> {
    const response = await axiosInstance.post(`/song-ads/${id}/generate-scenes`);
    return response.data.data.project;
  },

  /** 絵コンテ生成（非同期）。credit.check:image_generation。重複発行は409 */
  async generateStoryboard(projectId: string, sceneId: string): Promise<void> {
    await axiosInstance.post(`/song-ads/${projectId}/scenes/${sceneId}/storyboard`);
  },

  /** クリップ生成（非同期）。credit.check:video_generation。絵コンテ未生成422、同時実行超過429 */
  async generateClip(projectId: string, sceneId: string): Promise<void> {
    await axiosInstance.post(`/song-ads/${projectId}/scenes/${sceneId}/clip`);
  },

  /**
   * シーン一括更新（並び替え・追加・削除・motion_mode/motion_prompt・歌詞行・テロップ位置/サイズ）。
   * 送った配列の順がそのまま新しい display_order になる。
   */
  async updateScenes(id: string, scenes: UpdateSongAdSceneInput[]): Promise<SongAdProject> {
    const response = await axiosInstance.put(`/song-ads/${id}/scenes`, { scenes });
    return response.data.data.project;
  },

  /** シーン単位のAI修正（非同期job発行、202） */
  async aiEditScene(projectId: string, sceneId: string, instruction: string): Promise<void> {
    await axiosInstance.post(`/song-ads/${projectId}/scenes/${sceneId}/ai-edit`, { instruction });
  },

  /** モーション設定をAIにおまかせ（サビ・商品・CTA優先で全シーンへ motion_mode/motion_prompt を提案・適用） */
  async motionAuto(projectId: string): Promise<SongAdProject> {
    const response = await axiosInstance.post(`/song-ads/${projectId}/motion/auto`);
    return response.data.data.project;
  },

  /** シーン画像を商品画像（s3_key）に差し替える */
  async replaceSceneImage(projectId: string, sceneId: string, s3Key: string): Promise<SongAdProject> {
    const response = await axiosInstance.post(`/song-ads/${projectId}/scenes/${sceneId}/replace-image`, {
      s3_key: s3Key,
    });
    return response.data.data.project;
  },

  /**
   * 歌詞行ごとの歌唱タイミングを取得する（v4 B1/F8）。scenesに依存しないため、
   * シーン未生成でも「② 楽曲」段階で呼べる（「この曲で進める」で自動実行される想定）。
   * song_ready 必須（422 `song_required`）。既に取得済みなら再利用し、force=true のときだけ取り直す。
   */
  async fetchTimings(projectId: string, force = false): Promise<SongAdProject> {
    const response = await axiosInstance.post(
      `/song-ads/${projectId}/timings${force ? '?force=1' : ''}`
    );
    return response.data.data.project;
  },

  /**
   * テロップ初期生成（WhisperTimingProviderの行タイミングから CaptionSegment を生成し timeline に保存）。
   * song_ready 必須（422）。既に timeline.captions があれば 409（force=true で上書き）。
   */
  async initCaptions(projectId: string, force = false): Promise<SongAdProject> {
    const response = await axiosInstance.post(
      `/song-ads/${projectId}/captions/init${force ? '?force=1' : ''}`
    );
    return response.data.data.project;
  },

  /** 詳細編集タイムラインの自動保存（PATCH。楽観ロックなし・last-write-wins） */
  async updateTimeline(projectId: string, timeline: SongAdTimeline): Promise<SongAdProject> {
    const response = await axiosInstance.patch(`/song-ads/${projectId}/timeline`, { timeline });
    return response.data.data.project;
  },

  /** 合成開始（非同期job）。song_ready以降+絵コンテ全生成必須(422)、composing中は409 */
  async compose(id: string): Promise<void> {
    await axiosInstance.post(`/song-ads/${id}/compose`);
  },

  /**
   * 歌詞確定＋自動完走の開始（広告自動化マンの「この歌詞でいく」から呼ぶ・v7/F25）。
   * 歌詞の確定もここで行うため、事前の PUT /lyrics は不要。
   * 202で { project, estimate } を返す。402/403/404/409/422 はそのまま例外として投げるので、
   * 呼び出し側は extractAutopilotCreditDetails 等でエラー内容を判定すること。
   */
  async startAutoPilot(id: string, motionMode: MotionMode): Promise<{ project: SongAdProject; estimate: SongAdCreditPlanEstimate }> {
    const response = await axiosInstance.post(`/song-ads/${id}/autopilot`, { motion_mode: motionMode });
    return response.data.data;
  },

  extractErrorMessage,
  isCreditInsufficientError,
  isKanaLineMismatchError,
  extractAutopilotCreditShortfall,
};

export default songAdService;
